import { useMemo } from "react";

import { ActionIcon, Box, Flex, Select as MantineSelect, Table, TextInput } from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Button, NumberInput, Text } from "components";
import { AuthorityConstants } from "constant";
import { Bank, ContractPaymentResponse } from "types";

import { UserUtils } from "utils";

export interface ContractPaymentStructureProps {
  contractId: number | undefined;
  creditAmount: number | undefined;
  contractPayments: ContractPaymentResponse[];
  contractPaymentTypes: string[];
  availableBanks: Bank[];
  hasAmountMismatch: boolean;
  isAddingContractPayment: boolean;
  newContractPaymentType: string | null;
  newContractPaymentBank: Bank | null;
  newContractPaymentContractNumber: string;
  newContractPaymentAmount: number | undefined;
  isCreatingContractPayment: boolean;
  onAddContractPayment: () => void;
  onNewContractPaymentTypeChange: (value: string | null) => void;
  onNewContractPaymentBankChange: (value: Bank | null) => void;
  onNewContractPaymentContractNumberChange: (value: string) => void;
  onNewContractPaymentAmountChange: (value: number | undefined) => void;
  onCreateContractPayment: () => void;
  onDeleteContractPayment: (paymentId: number) => void;
  onResetNewContractPaymentForm: () => void;
  isDeletingPayment: (paymentId: number) => boolean;
}

export const ContractPaymentStructure = ({
  contractId,
  creditAmount,
  contractPayments,
  contractPaymentTypes,
  availableBanks,
  hasAmountMismatch,
  isAddingContractPayment,
  newContractPaymentType,
  newContractPaymentBank,
  newContractPaymentContractNumber,
  newContractPaymentAmount,
  isCreatingContractPayment,
  onAddContractPayment,
  onNewContractPaymentTypeChange,
  onNewContractPaymentBankChange,
  onNewContractPaymentContractNumberChange,
  onNewContractPaymentAmountChange,
  onCreateContractPayment,
  onDeleteContractPayment,
  onResetNewContractPaymentForm,
  isDeletingPayment,
}: ContractPaymentStructureProps) => {
  const { t } = useTranslation();
  const isContractNumberDisabled = newContractPaymentType === "PAYMENT";
  const remainingContractPaymentAmount = useMemo(() => {
    if (creditAmount == null) {
      return undefined;
    }

    const allocatedAmount = contractPayments.reduce((sum, payment) => sum + payment.amount, 0);

    return Math.max(0, Number((creditAmount - allocatedAmount).toFixed(2)));
  }, [creditAmount, contractPayments]);

  if (!UserUtils.hasAuthority(AuthorityConstants.ROLE_CON_PAY)) {
    return null;
  }

  return (
    <>
      <Text size="lg" fw={600} mt="xl" mb="md">
        {`${t("bankSelection.paymentStructure.title")} (${creditAmount?.toLocaleString("en-US") || 0} CHF)`}
      </Text>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("bankSelection.paymentStructure.type")}</Table.Th>
            <Table.Th>{t("bankSelection.paymentStructure.bank")}</Table.Th>
            <Table.Th>{t("bankSelection.paymentStructure.contractNumber")}</Table.Th>
            <Table.Th>{t("bankSelection.paymentStructure.amount")}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {contractPayments.length > 0 ? (
            contractPayments.map((payment) => (
              <Table.Tr key={payment.id}>
                <Table.Td>
                  {t(`bankSelection.paymentStructure.types.${payment.type}`, { defaultValue: payment.type })}
                </Table.Td>
                <Table.Td c={!payment.bank ? "dimmed" : undefined}>{payment.bank ?? "-"}</Table.Td>
                <Table.Td c={!payment.contractNumber ? "dimmed" : undefined}>{payment.contractNumber ?? "-"}</Table.Td>
                <Table.Td>{`${payment.amount.toFixed(2)} CHF`}</Table.Td>
                <Table.Td ta="right">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => onDeleteContractPayment(payment.id)}
                    loading={isDeletingPayment(payment.id)}
                    disabled={isCreatingContractPayment}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))
          ) : (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Text c="dimmed" size="sm">
                  {t("bankSelection.paymentStructure.empty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {isAddingContractPayment && (
            <Table.Tr>
              <Table.Td>
                <MantineSelect
                  data={contractPaymentTypes.map((value) => ({
                    value,
                    label: t(`bankSelection.paymentStructure.types.${value}`, { defaultValue: value }),
                  }))}
                  value={newContractPaymentType}
                  placeholder={t("bankSelection.paymentStructure.type")}
                  onChange={(value) => {
                    onNewContractPaymentTypeChange(value);

                    if (value === "PAYMENT") {
                      onNewContractPaymentContractNumberChange("");

                      if (
                        newContractPaymentAmount == null &&
                        remainingContractPaymentAmount != null
                      ) {
                        onNewContractPaymentAmountChange(remainingContractPaymentAmount);
                      }
                    }
                  }}
                  allowDeselect={false}
                />
              </Table.Td>
              <Table.Td>
                <MantineSelect
                  data={availableBanks.map((b) => ({ value: b, label: b }))}
                  value={newContractPaymentBank}
                  placeholder={t("bankSelection.paymentStructure.bank")}
                  onChange={(v) => onNewContractPaymentBankChange(v as Bank | null)}
                  clearable
                  searchable
                />
              </Table.Td>
              <Table.Td>
                <TextInput
                  value={newContractPaymentContractNumber}
                  onChange={(e) => onNewContractPaymentContractNumberChange(e.currentTarget.value)}
                  placeholder={t("bankSelection.paymentStructure.contractNumber")}
                  disabled={isContractNumberDisabled}
                />
              </Table.Td>
              <Table.Td>
                <NumberInput
                  value={newContractPaymentAmount}
                  onChange={(value) =>
                    onNewContractPaymentAmountChange(typeof value === "number" ? value : undefined)
                  }
                  allowDecimal={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  placeholder={t("bankSelection.paymentStructure.amount")}
                />
              </Table.Td>
              <Table.Td>
                <Flex justify="flex-end" gap="xs">
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="gray"
                    onClick={onResetNewContractPaymentForm}
                    disabled={isCreatingContractPayment}
                  >
                    {t("bankSelection.paymentStructure.cancel")}
                  </Button>
                  <Button
                    size="compact-xs"
                    onClick={onCreateContractPayment}
                    loading={isCreatingContractPayment}
                    disabled={!newContractPaymentType || newContractPaymentAmount == null}
                  >
                    {t("bankSelection.paymentStructure.save")}
                  </Button>
                </Flex>
              </Table.Td>
            </Table.Tr>
          )}
          {!isAddingContractPayment && (
            <Table.Tr>
              <Table.Td colSpan={5}>
                <Button
                  size="compact-xs"
                  variant="transparent"
                  c="var(--mantine-color-gray-6)"
                  onClick={onAddContractPayment}
                  disabled={!contractId}
                >
                  <>
                    <Box mr={4}><IconPlus size={16} /></Box>
                    {t("bankSelection.paymentStructure.add")}
                  </>
                </Button>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {hasAmountMismatch && (
        <Text ta="right" c="var(--mantine-color-error)" mt="xs">
          {t("credit.payment.diff")}
        </Text>
      )}
    </>
  );
};
